import { Formik } from "formik";
import { useTranslation } from "next-i18next";
import { useRouter } from "next/router";
import React, { Dispatch, SetStateAction } from "react";

import { UI_CONTEXTS } from "@/analytics/constants";
import EVENTS from "@/analytics/events";
import { trackUserEvent } from "@/analytics/tracking";

import { useCurrentLocale } from "@/context/LanguageContext";

import { Consumer, Recipient } from "../../../../interfaces/gift-cards/gift-card-interface";
import Footer from "../footer";
import FormOverlay from "../form-overlay";
import RecipientDetailsForm from "./recipient-details-form";

type FormErrors = {
    [field: string]: string | string[];
};

type Props = {
    setFadeOut: () => void;
    setConfirmOrder: Dispatch<SetStateAction<boolean>>;
    setConsumer: Dispatch<SetStateAction<Consumer>>;
    setRecipient: Dispatch<SetStateAction<Recipient>>;
    consumer: Consumer;
    recipient: Recipient;
    active?: boolean;
};

const emailRegex = new RegExp(".+\\@.+\\..+");

const SendToFriendModal: React.FC<Props> = ({
    setConfirmOrder,
    setConsumer,
    setRecipient,
    setFadeOut,
    consumer,
    recipient,
    active,
}) => {
    const initialValues = {
        consumerFirstname: consumer.firstname,
        consumerLastname: consumer.lastname,
        consumerEmail: consumer.email,
        consumerConfirmEmail: consumer.confirmEmail,
        recipientName: recipient.name,
        recipientEmail: recipient.email,
        recipientConfirmEmail: recipient.confirmEmail,
        recipientMessage: recipient.message,
    };
    const { t } = useTranslation();
    const locale = useCurrentLocale();
    const router = useRouter();

    const eventProps = {
        newFlow: false,
    };
    return (
        <Formik
            initialValues={initialValues}
            validate={(values) => {
                const errors: FormErrors = {};

                //Recipient validation
                if (!values.recipientEmail) {
                    errors.recipientEmail = t("giftCards.forms.errorMessages.required");
                } else if (!emailRegex.test(values.recipientEmail)) {
                    errors.recipientEmail = t("giftCards.forms.errorMessages.invalidEmail");
                } else if (values.recipientEmail !== values.recipientConfirmEmail) {
                    errors.recipientEmail = t("giftCards.forms.errorMessages.unconfirmedEmail");
                }

                if (!values.recipientConfirmEmail) {
                    errors.recipientConfirmEmail = t("giftCards.forms.errorMessages.required");
                } else if (!emailRegex.test(values.recipientConfirmEmail)) {
                    errors.recipientConfirmEmail = t("giftCards.forms.errorMessages.invalidEmail");
                } else if (values.recipientConfirmEmail !== values.recipientEmail) {
                    errors.recipientConfirmEmail = t(
                        "giftCards.forms.errorMessages.unconfirmedEmail"
                    );
                }

                if (!values.recipientName) {
                    errors.recipientName = t("giftCards.forms.errorMessages.required");
                }

                if (!values.recipientMessage) {
                    errors.recipientMessage = t("giftCards.forms.errorMessages.required");
                }

                //Buyer validation
                if (!values.consumerEmail) {
                    errors.consumerEmail = t("giftCards.forms.errorMessages.required");
                } else if (!emailRegex.test(values.consumerEmail)) {
                    errors.consumerEmail = t("giftCards.forms.errorMessages.invalidEmail");
                } else if (values.consumerEmail !== values.consumerConfirmEmail) {
                    errors.consumerEmail = t("giftCards.forms.errorMessages.unconfirmedEmail");
                }

                if (!values.consumerConfirmEmail) {
                    errors.consumerConfirmEmail = t("giftCards.forms.errorMessages.required");
                } else if (!emailRegex.test(values.consumerConfirmEmail)) {
                    errors.consumerConfirmEmail = t("giftCards.forms.errorMessages.invalidEmail");
                } else if (values.consumerConfirmEmail !== values.consumerEmail) {
                    errors.consumerConfirmEmail = t(
                        "giftCards.forms.errorMessages.unconfirmedEmail"
                    );
                }

                if (!values.consumerFirstname) {
                    errors.consumerFirstname = t("giftCards.forms.errorMessages.required");
                }

                if (!values.consumerLastname) {
                    errors.consumerLastname = t("giftCards.forms.errorMessages.required");
                }

                return errors;
            }}
            onSubmit={(values, { setSubmitting }) => {
                trackUserEvent(
                    locale,
                    router.asPath,
                    UI_CONTEXTS.GIFTCARD_SENDASGIFT_FORM,
                    EVENTS.GIFT_CARD_SEND_AS_GIFT_CONTINUE_CLICK,
                    eventProps
                );
                const {
                    consumerFirstname,
                    consumerLastname,
                    consumerEmail,
                    consumerConfirmEmail,
                    recipientName,
                    recipientEmail,
                    recipientConfirmEmail,
                    recipientMessage,
                } = values;
                setTimeout(() => {
                    setSubmitting(false);
                    setConfirmOrder(true);
                    setFadeOut();
                    setRecipient({
                        name: recipientName,
                        email: recipientEmail,
                        confirmEmail: recipientConfirmEmail,
                        message: recipientMessage,
                    });
                    setConsumer({
                        firstname: consumerFirstname,
                        lastname: consumerLastname,
                        email: consumerEmail,
                        confirmEmail: consumerConfirmEmail,
                    });
                }, 400);
            }}
        >
            {(formikProps) => (
                <FormOverlay
                    headerText={t("giftCards.forms.buyForFriend.header")}
                    body={<RecipientDetailsForm formikProps={formikProps} />}
                    footer={<Footer {...formikProps} />}
                    active={active}
                    setFadeOut={setFadeOut}
                />
            )}
        </Formik>
    );
};

export default SendToFriendModal;
